import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Pagination } from '../../components/ui/pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Search, Download, Database, FileText, CheckCircle, Clock } from 'lucide-react';
import { formatDate } from '../../lib/utils';

const PAGE_SIZE = 50;

type TableTab = 'vendors' | 'extracted' | 'suggested' | 'confirmed';

const TABLE_META: Record<TableTab, { label: string; icon: React.ReactNode; color: string; description: string }> = {
  vendors: {
    label: 'Vendor Catalog',
    icon: <Database className="h-4 w-4" />,
    color: 'blue',
    description: 'Master vendor catalog synced from Vantage data catalog',
  },
  extracted: {
    label: 'Extracted Vendors',
    icon: <FileText className="h-4 w-4" />,
    color: 'orange',
    description: 'Vendor data extracted by Vantage from invoices',
  },
  suggested: {
    label: 'Suggested Vendors',
    icon: <Clock className="h-4 w-4" />,
    color: 'yellow',
    description: 'Auto-matched vendor suggestions awaiting review',
  },
  confirmed: {
    label: 'Confirmed Vendors',
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'green',
    description: 'Vendors confirmed through matching or manual correction',
  },
};

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) return formatDate(value);
  if (typeof value === 'string' && value.match(/^[0-9a-f]{8}-[0-9a-f]{4}-/i)) return value.substring(0, 8) + '...';
  return String(value);
}

function renderTableHeaders(columns: string[]): React.ReactNode {
  return (
    <TableRow>
      {columns.map((col) => (
        <TableHead key={col} className="whitespace-nowrap text-xs">
          {col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
        </TableHead>
      ))}
    </TableRow>
  );
}

function renderTableRow(row: Record<string, unknown>, columns: string[]): React.ReactNode {
  return (
    <TableRow key={row.id?.toString() || Math.random().toString(36).slice(2)}>
      {columns.map((col) => {
        const val = row[col];
        if (col === 'status') {
          return (
            <TableCell key={col}>
              <Badge variant={val === 'confirmed' ? 'success' : val === 'pending' ? 'secondary' : 'destructive'}>
                {String(val)}
              </Badge>
            </TableCell>
          );
        }
        if (col === 'confidence') {
          return (
            <TableCell key={col}>
              <Badge variant={Number(val) >= 80 ? 'success' : Number(val) >= 60 ? 'secondary' : 'destructive'}>
                {val !== null && val !== undefined ? `${val}%` : '—'}
              </Badge>
            </TableCell>
          );
        }
        return <TableCell key={col} className="text-sm">{formatCellValue(val)}</TableCell>;
      })}
    </TableRow>
  );
}

function DataTable({ id, table }: { id: string; table: TableTab }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const meta = TABLE_META[table];

  function handleSearchChange(value: string) {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['smart-correct-data', id, table, page, debouncedSearch],
    queryFn: () =>
      api
        .get(`/api/smart-correct/${id}/data/${table}`, {
          params: {
            offset: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
            search: debouncedSearch || undefined,
          },
        })
        .then((r) => r.data),
    enabled: !!id,
  });

  const rows: Record<string, unknown>[] = data?.query?.data ?? [];
  const total = data?.query?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Get column order from first row
  const columns = rows.length > 0 ? Object.keys(rows[0] as Record<string, unknown>) : [];

  function handleExport() {
    window.open(`/api/smart-correct/${id}/data/${table}/export`, '_blank');
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            {meta.icon} {meta.label}
          </h3>
          <p className="text-sm text-gray-500">{meta.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{total.toLocaleString()} records</span>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={total === 0}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder={`Search ${meta.label.toLowerCase()}...`}
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10 max-w-sm"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading {meta.label.toLowerCase()}...</div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="text-gray-300 mb-4">{meta.icon}</div>
          <h3 className="text-lg font-semibold mb-2">No {meta.label.toLowerCase()}</h3>
          <p className="text-gray-500 text-sm">Data will appear here once available.</p>
        </Card>
      ) : (
        <>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>{renderTableHeaders(columns)}</TableHeader>
                <TableBody>
                  {rows.map((row) => renderTableRow(row, columns))}
                </TableBody>
              </Table>
            </div>
          </Card>
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function SmartCorrectDataPage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<TableTab>('vendors');

  const { data: service } = useQuery({
    queryKey: ['smart-correct', id],
    queryFn: () =>
      api
        .get('/api/smart-correct/get', { params: { service_instance_id: id } })
        .then((r) => r.data),
    enabled: !!id,
  });

  const s = service?.query?.[0] ?? service;
  const tables = s?.config?.tables;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/smart-correct/${id}`}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6 text-blue-600" />
            Data Browser
          </h1>
          <p className="text-gray-500 text-sm">
            {s?.service_title ?? 'Smart Correct'} — Browse & export table data
          </p>
        </div>
      </div>

      {/* Table summary cards */}
      {tables && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {(['vendors', 'extracted', 'suggested', 'confirmed'] as TableTab[]).map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`text-left p-3 rounded-lg border-2 transition-colors ${
                activeTab === t
                  ? t === 'vendors'
                    ? 'border-blue-500 bg-blue-50'
                    : t === 'extracted'
                    ? 'border-orange-500 bg-orange-50'
                    : t === 'suggested'
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-green-500 bg-green-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                {TABLE_META[t].icon} {TABLE_META[t].label}
              </div>
              <p className="text-xs text-gray-400 mt-1 font-mono">{tables[t.replace('extracted', 'extracted_vendor').replace('suggested', 'suggested_vendor').replace('confirmed', 'confirmed_vendor') as keyof typeof tables] ?? '—'}</p>
            </button>
          ))}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TableTab)}>
        <TabsList>
          <TabsTrigger value="vendors">
            <Database className="h-4 w-4 mr-1" /> Vendors
          </TabsTrigger>
          <TabsTrigger value="extracted">
            <FileText className="h-4 w-4 mr-1" /> Extracted
          </TabsTrigger>
          <TabsTrigger value="suggested">
            <Clock className="h-4 w-4 mr-1" /> Suggested
          </TabsTrigger>
          <TabsTrigger value="confirmed">
            <CheckCircle className="h-4 w-4 mr-1" /> Confirmed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vendors" className="mt-4">
          <DataTable id={id!} table="vendors" />
        </TabsContent>
        <TabsContent value="extracted" className="mt-4">
          <DataTable id={id!} table="extracted" />
        </TabsContent>
        <TabsContent value="suggested" className="mt-4">
          <DataTable id={id!} table="suggested" />
        </TabsContent>
        <TabsContent value="confirmed" className="mt-4">
          <DataTable id={id!} table="confirmed" />
        </TabsContent>
      </Tabs>
    </div>
  );
}